import { getCountryForTimezone } from 'countries-and-timezones';

export enum CountryCode {
  US = 'US',
  MX = 'MX',
  CA = 'CA',
  AU = 'AU',
  ZA = 'ZA',
  BR = 'BR',
  KZ = 'KZ',
  FR = 'FR',
  DE = 'DE',
  GB = 'GB',
  IE = 'IE',
  ES = 'ES',
}

export enum CallingCode {
  US = '+1',
  MX = '+52',
  CA = '+1',
  AU = '+61',
  ZA = '+27',
  BR = '+55',
  KZ = '+7',
  FR = '+33',
  DE = '+49',
  GB = '+44',
  IE = '+353',
  ES = '+34',
}

export interface Phone {
  countryCode: CountryCode;
  callingCode: CallingCode;
  phoneNumber: string;
}

const SUPPORTED_COUNTRIES = new Set<string>(Object.values(CountryCode));

/**
 * Maps an IANA timezone to a supported {@link CountryCode}, falling back to
 * {@link CountryCode.US} for the US and any timezone whose country isn't
 * supported by the picker.
 */
export const getCountryFromTimeZone = (
  timeZone?: string | null
): CountryCode => {
  const country = timeZone ? getCountryForTimezone(timeZone)?.id : undefined;

  return country && SUPPORTED_COUNTRIES.has(country)
    ? (country as CountryCode)
    : CountryCode.US;
};

/**
 * Auto-detected default country for the phone picker, from the device timezone.
 * Timezone is a pure-JS signal (Intl, no native module), so preselection ships
 * over-the-air; it also reflects the device region, which the UI-language
 * locale often does not (iOS commonly reports a region-less 'en'). Falls back
 * to {@link CountryCode.US} when the timezone is unavailable.
 */
export const getDefaultPhoneCountry = (): CountryCode => {
  try {
    return getCountryFromTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  } catch {
    return CountryCode.US;
  }
};
